import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { NotificationType } from 'common/enums';
import { roleChoices, roleLabel, STATUS_LABEL } from 'common/share-dialog';
import { UserInitials } from 'common/user-initials';
import { appActions, AppContext } from 'context/app-context';
import { classnames, getInitials } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import {
  ShareEntry,
  ShareRole,
  SharesController,
  shareErrorDetails,
} from 'networking/controllers/shares-controller';
import styles from './people-table.module.scss';

interface PeopleTableProps {
  vaultId: number;
  vaultTitle: string;
  /** Bump to load the list again (e.g. after inviting from a dialog). */
  refreshKey?: number;
  /** At most this many rows until "See all". */
  limit?: number;
}

// Everyone a vault, its albums or its memories are shared with: their role,
// what it applies to, where the invitation stands; and changing or removing
// them (for those who may). Vault Settings and the Collaborators page.
export const PeopleTable = ({
  vaultId,
  vaultTitle,
  refreshKey = 0,
  limit,
}: PeopleTableProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [entries, setEntries] = useState<ShareEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const showRemovedId = useId();

  const notify = useCallback(
    (title: string, type: NotificationType, message?: string) =>
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title,
          type,
          message,
        }),
      }),
    [dispatch],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    SharesController.list({
      type: 'vault',
      vaultId,
      id: vaultId,
      name: vaultTitle,
    })
      .then((result) => {
        if (cancelled) return;
        setEntries(result.shares);
        setFailed(false);
      })
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [vaultId, vaultTitle, refreshKey]);

  const rows = useMemo(() => {
    const rank = (entry: ShareEntry) =>
      (({ active: 0, invited: 1 }) as Record<string, number>)[entry.status] ??
      2;
    const visible = entries
      .filter((entry) => showRemoved || entry.status !== 'removed')
      .sort((a, b) => rank(a) - rank(b));
    return limit && !showAll ? visible.slice(0, limit) : visible;
  }, [entries, showRemoved, showAll, limit]);

  const hiddenCount =
    limit && !showAll
      ? entries.filter((e) => showRemoved || e.status !== 'removed').length -
        rows.length
      : 0;

  const changeRole = async (entry: ShareEntry, role: ShareRole) => {
    try {
      const updated = await SharesController.changeRole(entry.id, role);
      setEntries((list) =>
        list.map((item) =>
          item.id === entry.id ? { ...item, ...updated } : item,
        ),
      );
      // Takes effect right away; nothing is emailed.
      notify(
        `${entry.name || entry.email} is now ${roleLabel(
          role,
          entry.resource.type,
        ).toLowerCase()}`,
        NotificationType.Success,
        'The change applies right away.',
      );
    } catch (error) {
      notify(
        'The role could not be changed',
        NotificationType.Error,
        shareErrorDetails(error) || undefined,
      );
    }
  };

  const remove = async (entry: ShareEntry) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Remove ${entry.name || entry.email}? They lose access to ${
          entry.resource.type === 'vault'
            ? 'the vault'
            : `“${entry.resource.name}”`
        }.`,
      )
    ) {
      return;
    }
    try {
      await SharesController.remove(entry.id);
      setEntries((list) =>
        list.map((item) =>
          item.id === entry.id ? { ...item, status: 'removed' } : item,
        ),
      );
    } catch {
      notify('They could not be removed', NotificationType.Error);
    }
  };

  const resend = async (entry: ShareEntry) => {
    try {
      await SharesController.resend(entry.id);
      notify('Invitation sent again', NotificationType.Success);
    } catch {
      notify('The invitation could not be sent', NotificationType.Error);
    }
  };

  if (loading && entries.length === 0) {
    return <div className={styles.message}>Loading…</div>;
  }
  if (failed) {
    return (
      <div className={styles.message}>
        Only the vault&apos;s owner and editors can see who it&apos;s shared
        with.
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {rows.length === 0 ? (
        <div className={styles.message}>Not shared with anyone yet.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Access to</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const isYou =
                entry.userId != null && entry.userId === state.data.user.id;
              const display = `${entry.name || entry.email || ''}${
                isYou ? ' (you)' : ''
              }`;
              const editable = entry.canManage && entry.status !== 'removed';
              const options = roleChoices(entry.role, entry.resource.type);
              return (
                <tr
                  key={entry.id}
                  className={classnames({
                    [styles.rowInactive]: [
                      'removed',
                      'declined',
                      'expired',
                    ].includes(entry.status),
                  })}
                >
                  <td>
                    <div className={styles.person}>
                      <span className={styles.avatar}>
                        <UserInitials
                          name={getInitials(entry.name || entry.email || '')}
                        />
                      </span>
                      <div className={styles.personText}>
                        <span className={styles.name}>{display}</span>
                        {entry.name && entry.email && (
                          <span className={styles.email}>{entry.email}</span>
                        )}
                      </div>
                      {entry.status !== 'active' && (
                        <span
                          className={classnames(
                            styles.badge,
                            styles[`badge_${entry.status}`],
                          )}
                        >
                          {STATUS_LABEL[entry.status]}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {editable ? (
                      <div className={styles.selectWrap}>
                        <select
                          className={styles.select}
                          aria-label={`Role of ${display}`}
                          value={entry.role}
                          onChange={(e) =>
                            changeRole(entry, e.target.value as ShareRole)
                          }
                        >
                          {options.map((option) => (
                            <option key={option.role} value={option.role}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      roleLabel(entry.role, entry.resource.type)
                    )}
                  </td>
                  <td>
                    {entry.resource.type === 'vault'
                      ? 'Whole vault'
                      : `${entry.resource.type === 'album' ? 'Album' : 'Memory'}: ${
                          entry.resource.name || 'untitled'
                        }`}
                  </td>
                  <td>
                    {editable && (
                      <div className={styles.actions}>
                        {entry.status === 'invited' && (
                          <button
                            type="button"
                            className={styles.action}
                            onClick={() => resend(entry)}
                          >
                            Resend
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.action}
                          onClick={() => remove(entry)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className={styles.footer}>
        {hiddenCount > 0 && (
          <button
            type="button"
            className={styles.action}
            onClick={() => setShowAll(true)}
          >
            See all ({hiddenCount} more)
          </button>
        )}
        {entries.some((entry) => entry.status === 'removed') && (
          <label className={styles.toggle} htmlFor={showRemovedId}>
            <input
              id={showRemovedId}
              type="checkbox"
              checked={showRemoved}
              onChange={(e) => setShowRemoved(e.target.checked)}
            />
            Show removed people
          </label>
        )}
      </div>
    </div>
  );
};
